import { ChangeEvent, SelectHTMLAttributes, forwardRef } from 'react'
import { cn } from '../utils/cn'
import { ChevronDown } from 'lucide-react'
import { SearchableSelect, SEARCHABLE_FROM } from './SearchableSelect'

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  options: { value: string; label: string }[]
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, id, ...props }, ref) => {
    // Long lists (countries, health insurers, provinces …) can be typed into instead of scrolled
    if (options.length > SEARCHABLE_FROM) {
      return (
        <SearchableSelect
          id={id}
          label={label}
          error={error}
          options={options}
          value={String(props.value ?? '')}
          disabled={props.disabled}
          className={className}
          // Callers read e.target.value only — hand them the same shape a native select would
          onChange={value => props.onChange?.({ target: { value }, currentTarget: { value } } as unknown as ChangeEvent<HTMLSelectElement>)}
        />
      )
    }
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={id} className="label-text">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={id}
            className={cn(
              'input-field appearance-none pr-10',
              error && 'border-destructive focus:ring-destructive',
              className
            )}
            {...props}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
        </div>
        {error && (
          <p className="mt-1 text-small text-destructive">{error}</p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'
